/*

  Wait.ts

  Code for the WaitDialog and AcceptDialog components,
  used in the UI flow for timed games

  Copyright (C) 2024 Miðeind ehf.
  Original author: Vilhjálmur Þorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

*/

import { IView } from "./types";
import { m, Component } from "./mithril";
import { mt, ts } from "./i18n";
import { request } from "./request";
import { DialogButton, OnlinePresence } from "./components";
import { glyph } from "./util";
import { attachFirebaseListener, detachFirebaseListener } from "./channel";
import { StatsView } from "./statsview";

interface IWaitDialogState {
  oppOnline: boolean;
  pointOfNoReturn: boolean;
  firebasePath: string;
}

interface IWaitDialogAttributes {
  view: IView;
  oppId: string;
  oppNick: string;
  oppName: string;
  duration: number;
  challengeKey: string;
}

export const WaitDialog: Component<IWaitDialogAttributes, IWaitDialogState> = {

  // A dialog that is shown while the user waits for the opponent,
  // who issued a timed game challenge, to be ready

  oninit: (vnode) => {
    const { state } = vnode;
    state.oppOnline = false;
    state.pointOfNoReturn = false;
    state.firebasePath = "";
  },

  oncreate: (vnode) => {
    // Note: we must use a two-step initialization here,
    // calling oninit() first and then oncreate(), since calls to
    // m.redraw() - explicit or implicit via m.request() - from within
    // oninit() will cause a recursive call to oninit(), resulting in
    // a double API call to the backend.
    const { attrs, state } = vnode;
    const { view, oppId, challengeKey: key } = attrs;
    const { model } = view;
    const globalState = model.state;
    const userId = model.state?.netskraflUserId ?? "";

    if (!userId || !oppId) return; // Should not happen

    state.firebasePath = `user/${userId}/wait/${oppId}`;

    // Initiate an online check on the opponent (fire-and-forget)
    const updateOnline = async () => {
      try {
        if (key && globalState) {
          const json: { online: boolean; waiting: boolean; } = await request(globalState, {
            method: "POST",
            url: "/initwait",
            body: { opp: oppId, key }
          });
          // If json.waiting is false, the initiation failed
          // and there is really no point in continuing to wait
          if (json && json.online && json.waiting) {
            // The user is online
            state.oppOnline = true;
          }
        }
      }
      catch(e) {
      }
    };
    updateOnline(); // Fire-and-forget, don't await

    // Attach a Firebase listener to the wait path
    attachFirebaseListener(state.firebasePath, (json: true | { game: string }) => {
      if (json !== true && json.game) {
        // A new game has been created and initiated by the server
        state.pointOfNoReturn = true;
        detachFirebaseListener(state.firebasePath);
        // We don't need to pop the dialog; that is done automatically
        // by the route resolver upon m.route.set()
        // Navigate to the newly initiated game
        m.route.set("/game/" + json.game);
      }
    });
  },

  view: (vnode) => {
    const { attrs, state } = vnode;
    const { view, oppId, duration, challengeKey: key, oppNick, oppName } = attrs;
    const { model } = view;
    const globalState = model.state;
    const userId = model.state?.netskraflUserId ?? "";

    if (!globalState) return null;

    const cancelWait = async () => {
      // Cancel a pending wait for a timed game
      if (!globalState) return;
      try {
        request(globalState, {
          method: "POST",
          url: "/cancelwait",
          body: {
            user: userId,
            opp: oppId,
            key
          }
        });
      }
      catch(e) {
      }
    };

    return m(".modal-dialog",
      { id: "wait-dialog", style: { visibility: "visible" } },
      m(".ui-widget.ui-widget-content.ui-corner-all", { "id": "wait-form" },
        [
          m(".chall-hdr",
            m("table",
              m("tbody",
                m("tr", [
                  m("td", m("h1.chall-icon", glyph("time"))),
                  m("td.l-border", [
                    m(OnlinePresence,
                      { id: "chall-online", userId: oppId, online: state.oppOnline, state: globalState }
                    ),
                    m("h1", oppNick),
                    m("h2", oppName),
                  ])
                ])
              )
            )
          ),
          m(".wait-explain", [
            mt("p", [
              "Þú ert reiðubúin(n) að taka áskorun um viðureign með klukku,",
            ]),
            m("p", [
              m("strong", [ "2 x ", duration.toString(), ts(" mínútur.") ])
            ]),
            mt("p", [
              "Beðið er eftir að áskorandinn ", m("strong", oppNick),
              " sé ", state.oppOnline ? "" : mt("span#chall-is-online", "álínis og "), "til í tuskið."
            ]),
            mt("p", "Leikur hefst um leið og áskorandinn bregst við. Handahóf ræður hvor byrjar."),
            mt("p", "Ef þér leiðist biðin geturðu hætt við og reynt aftur síðar.")
          ]),
          m(DialogButton,
            {
              id: "wait-cancel",
              title: ts("Hætta við"),
              onclick: (ev: Event) => {
                // Cancel the wait status and navigate back to the main page
                if (state.pointOfNoReturn) {
                  // Actually, it's too late to cancel
                  ev.preventDefault();
                  return;
                }
                if (state.firebasePath)
                  detachFirebaseListener(state.firebasePath);
                cancelWait();
                view.popDialog();
                ev.preventDefault();
              }
            },
            glyph("remove")
          )
        ]
      )
    );
  }

};

interface IAcceptDialogState {
  oppReady: boolean;
}

interface IAcceptDialogAttributes {
  view: IView;
  oppId: string;
  oppNick: string;
  challengeKey: string;
}

export const AcceptDialog: Component<IAcceptDialogAttributes, IAcceptDialogState> = {

  // A dialog that is shown (usually briefly) while
  // the user who originated a timed game challenge
  // is linked up with her opponent and a new game is started

  oninit: async (vnode) => {
    const { attrs, state } = vnode;
    const { view, oppId, challengeKey: key } = attrs;
    const globalState = view.model.state;
    if (!globalState) return; // Should not happen

    state.oppReady = true;

    // Initiate a wait status check on the opponent
    try {
      const json: { waiting: boolean; } = await request(globalState, {
        method: "POST",
        url: "/waitcheck",
        body: { user: oppId, key }
      });
      if (json?.waiting) {
        // Both players are now ready: Start the timed game.
        // The newGame() call switches to a new route (/game),
        // and all open dialogs are thereby closed automatically.
        view.actions.startNewGame(oppId, true);
      }
      else {
        // Something didn't check out: keep the dialog open
        // until the user manually closes it
        state.oppReady = false;
      }
    }
    catch(e) {
      state.oppReady = false;
    }
  },

  view: (vnode) => {
    const { attrs, state } = vnode;
    const { view, oppNick } = attrs;

    return m(".modal-dialog",
      { id: "accept-dialog", style: { visibility: "visible" } },
      m(".ui-widget.ui-widget-content.ui-corner-all", { id: "accept-form" },
        [
          m(".chall-hdr",
            m("table",
              m("tbody",
                m("tr",
                  [
                    m("td", m("h1.chall-icon", glyph("time"))),
                    m("td.l-border", m("h1", oppNick))
                  ]
                )
              )
            )
          ),
          m("div", { "style": { "text-align": "center", "padding-top": "32px" }},
            [
              m("p", mt("strong", "Viðureign með klukku")),
              mt("p",
                state.oppReady ? "Athuga hvort andstæðingur er reiðubúinn..."
                : ["Andstæðingurinn ", m("strong", oppNick), " er ekki reiðubúinn"]
              )
            ]
          ),
          m(DialogButton,
            {
              id: 'accept-cancel',
              title: ts('Reyna síðar'),
              onclick: (ev: Event) => {
                // Abort mission
                view.popDialog();
                ev.preventDefault();
              }
            },
            glyph("remove")
          )
        ]
      )
    );
  }

};
